import LegalPageLayout from "../components/LegalPageLayout";

const LAST_UPDATED = "February 11, 2026";

/**
 * Terms & Conditions for order.screenzz.com.
 * Custom-DIY focused — protects ScreenzZ from rebuild claims caused by
 * customer-supplied incorrect measurements and from improper self-install.
 */
export default function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms & Conditions"
      subtitle="These Terms & Conditions govern your use of order.screenzz.com and any custom screen door or window screen order placed through the platform. By placing an order, you agree to these terms."
      lastUpdated={LAST_UPDATED}
    >
      <Section title="1. Custom Order Policy">
        <p>
          All screen products sold through order.screenzz.com are custom-built to customer-provided
          measurements and selected specifications. Because products are custom made,
          <strong> orders cannot be canceled, refunded, or returned once production has begun.</strong>
        </p>
        <p>
          If you need to change your order, contact us immediately. We will do our best to accommodate
          changes before production starts.
        </p>
      </Section>

      <Section title="2. Measurement Responsibility">
        <p>
          <strong>Customers are responsible for verifying all measurements before ordering.</strong>
          ScreenzZ builds products exactly to the measurements you submit. We do not adjust, interpret,
          or modify customer-supplied measurements unless you have selected our optional Pro Measurement
          service.
        </p>
        <p>
          <strong>Incorrect measurements may require a rebuild at customer expense.</strong> If your
          screen or door does not fit due to a measurement provided by you, ScreenzZ is not responsible
          for the cost of remaking the product.
        </p>
        <p>
          If you are not comfortable measuring, please choose our Pro Measurement add-on at checkout and
          we'll handle it for you.
        </p>
      </Section>

      <Section title="3. Installation Disclaimer">
        <p>
          <strong>DIY products are intended for customer self-installation.</strong> ScreenzZ is not
          responsible for:
        </p>
        <ul>
          <li>Improper installation</li>
          <li>Damage to existing door frames, window frames, or surrounding materials</li>
          <li>Compatibility issues caused by incorrect measurements or installation methods</li>
          <li>Injury or property damage resulting from self-installation</li>
        </ul>
        <p>
          For full peace of mind, our Delivery + Install service includes professional installation in
          our service area.
        </p>
      </Section>

      <Section title="4. Pickup & Delivery">
        <p><strong>Pickup:</strong> Orders selected for pickup will be ready at our Murrieta shop. We'll text and email you when your order is ready. Pickup is free. Bring a photo ID and your order number.</p>
        <p><strong>Local delivery:</strong> Delivery is available within our service area at the rate shown at checkout. We will contact you to schedule a delivery window after your order is built.</p>
        <p><strong>Reasonable delays:</strong> Build times are estimates. Material shortages, weather, holidays, or unusually high order volume can extend lead times. We'll communicate any meaningful delays.</p>
        <p><strong>Availability:</strong> For delivery and install, you (or an authorized adult) must be available at the delivery address during the scheduled window. Repeat failed delivery attempts may result in a re-delivery fee.</p>
      </Section>

      <Section title="5. SMS Terms">
        <p>
          Customers who provide their phone number and opt into SMS consent may receive transactional
          text messages related to orders, scheduling, measurement appointments, production updates,
          pickup notifications, delivery updates, and customer support.
        </p>
        <p>
          <strong>Consent is not a condition of purchase.</strong> Message frequency varies. Message and
          data rates may apply. Reply <strong>STOP</strong> to unsubscribe or <strong>HELP</strong> for
          assistance.
        </p>
        <p>
          For full details on how we handle your phone number and consent, see our{" "}
          <a href="/privacy-policy" className="brand-text font-semibold underline underline-offset-2">
            Privacy Policy
          </a>.
        </p>
      </Section>

      <Section title="6. Warranty & Damage Claims">
        <p>
          <strong>Inspect on receipt.</strong> Customers must inspect products upon pickup or delivery
          and report any visible defects or shipping damage at that time, or within
          <strong> 72 hours</strong> of receipt.
        </p>
        <p>
          ScreenzZ warrants its custom screens and doors against defects in materials and workmanship
          for a reasonable period under normal residential use. The warranty does not cover:
        </p>
        <ul>
          <li>Damage caused by incorrect measurements supplied by the customer</li>
          <li>Damage caused by improper installation</li>
          <li>Normal wear and tear, pet damage, or accidental damage</li>
          <li>Modifications made to the product after delivery</li>
        </ul>
        <p>To file a claim, contact us with your order number, a description of the issue, and clear photos.</p>
      </Section>

      <Section title="7. Pricing & Payment">
        <p>
          All prices are shown in U.S. dollars and are subject to applicable sales tax. Pricing on the
          site is the controlling price at the time you place your order. Promotions, discounts, and
          bulk pricing tiers are subject to their stated terms and may be changed or ended at any time.
        </p>
        <p>
          Payment is required in full at checkout. Payments are processed securely through Stripe and,
          in limited cases, Square. By submitting payment, you authorize ScreenzZ to charge the amount
          shown to your selected payment method.
        </p>
      </Section>

      <Section title="8. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, ScreenzZ's total liability arising out of or in
          connection with your order is limited to the amount you paid for the product giving rise to
          the claim. ScreenzZ is not liable for indirect, incidental, or consequential damages, including
          lost profits, lost time, or damage to property unrelated to the product itself.
        </p>
        <p>
          Nothing in these terms limits any rights you have that cannot be waived under applicable
          consumer protection law.
        </p>
      </Section>

      <Section title="9. Governing Law">
        <p>
          These terms are governed by the laws of the State of California, without regard to its
          conflict-of-law principles. Any dispute arising under these terms shall be resolved in the
          state or federal courts located in Riverside County, California.
        </p>
      </Section>

      <Section title="10. Changes to These Terms">
        <p>
          We may update these Terms from time to time. When we do, we'll update the &quot;Last
          updated&quot; date at the top of this page. Continued use of order.screenzz.com after a change
          means you accept the updated Terms.
        </p>
      </Section>
    </LegalPageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl sm:text-2xl font-extrabold text-[var(--brand-text)] mb-3">{title}</h2>
      <div className="text-[15px] leading-relaxed space-y-3 muted-text">{children}</div>
    </section>
  );
}
